<template>
	<v-col
			ref="bwImage"
			class="select-image"
			:class="{
				'flex-row': !showUploader,
				'd-flex flex-column justify-center': showUploader
			}"
			cols="12"
			:lg="showUploader ? 12 : localCols.lg"
			:md="showUploader ? 12 : localCols.md"
			:sm="showUploader ? 12 : localCols.sm"
			:style="{
				height: $vuetify.breakpoint.mdAndUp ? 'calc(100% + 2rem)' : 'calc(100% + 1rem)'
			}"
	>
		<div v-if="label">
			<h6 class="font-weight-medium text-body-2">
				{{ label }}
			</h6>
			<v-divider class="mb-2"/>
		</div>

		<template v-if="showUploader && !disabled">
			<bw-image-upload
					v-model="uploadedImage"
					:is-admin="isAdmin"
					:is-super-admin="isSuperAdmin"
					:required="!uploadedImage"
					:upload-assignment="uploadAssignment"
					:upload-assignment-id="uploadAssignmentId"
					:cropper-settings="value?.cropperSettings"
					:max-file-size="value?.maxFileSize || '5mb'"
					:min-dimensions="value.minDimensions"
					@applied="applyImg"
					@canceled="closeCropper"
			/>
		</template>

		<template v-else>
			<div class="position-relative">
				<template v-if="hasImage && showRemove && !isGoogle">
					<div class="remove-image">
						<v-btn
								aria-label="Remove Image"
								class="rounded"
								:disabled="disabled"
								depressed
								icon
								small
								:style="{ backgroundColor: 'rgba(255,0,0,0.9)', color: 'white' }"
								@click="removeImage"
						>
							<v-icon size="20">mdi-trash-can-outline</v-icon>
						</v-btn>
					</div>
				</template>
				<v-img
						v-if="showTab === 'image'"
						:aspectRatio="aspectRatio || null"
						:contain="!cover || null"
						:cover="cover || null"
						class="grey lighten-4 rounded-lg"
						:src="value.url"
						:lazy-src="`https://placehold.co/${value.minDimensions}?text=Loading`"
				></v-img>
				<video
						v-if="showTab === 'video' && (value.originalURL || value.url)"
						class="w-100"
						controls
						:src="value.originalURL || value.url"
				>
					Your browser does not support the video tag.
				</video>
				<audio
						v-if="showTab === 'audio' && (value.originalURL || value.url)"
						class="w-100"
						controls
						:src="value.originalURL || value.url"
				>
					Your browser does not support the audio tag.
				</audio>
			</div>

			<template v-if="!hideEdit">
				<div
						class="edit-image align-center d-flex mt-2 grey lighten-4 rounded-lg px-2 py-1">
					<div class="flex-grow-1 text-caption">
						<template v-if="isGoogle">
							<v-icon size="20">mdi-google</v-icon>
							Google Image
						</template>
						<template v-else>
							{{ hasImage ? 'Edit' : 'Upload' }} {{ Bandwango.capitalize(type) }}:
						</template>
					</div>
					<div class="d-flex justify-end">
						<v-btn
								aria-label="Change Image"
								class="rounded"
								color="secondary"
								depressed
								:disabled="disabled"
								icon
								small
								@click="changeImg()"
						>
							<v-icon v-if="hasImage" size="20">mdi-undo-variant</v-icon>
							<v-icon v-else size="20">mdi-upload</v-icon>
						</v-btn>
						<template v-if="showTab === 'image'">
							<v-divider
									v-if="hasImage && !isGoogle"
									class="mx-2 my-1"
									vertical
							></v-divider>
							<v-btn
									v-if="hasImage && !isGoogle"
									aria-label="Recrop Image"
									class="rounded"
									color="secondary"
									:disabled="disabled"
									depressed
									icon
									small
									@click="recropImg()"
							>
								<v-icon size="20">mdi-crop</v-icon>
							</v-btn>
						</template>
					</div>
				</div>
			</template>

		</template>
	</v-col>
</template>

<script>
import BwImageUpload from "./BwImageUpload";

export default {
	name: 'BwImage',
	components: {
		BwImageUpload,
	},
	props: {
		aspectRatio: [String, Number, Boolean],
		autoShowUploader: {
			type: Boolean,
			default: true
		},
		contain: {
			type: Boolean,
			default: true
		},
		cols: {
			type: Object,
			default: () => ({
				lg: 3,
				md: 4,
				sm: 6
			})
		},
		cover: Boolean,
		disabled: {
			type: Boolean,
			default: false
		},
		hideEdit: Boolean,
		isAdmin: Boolean,
		isSuperAdmin: Boolean,
		label: {
			type: String,
			default: null
		},
		stockImage: {
			type: Number,
			default: 1,
		},
		showRemove: Boolean,
		type: {
			type: String,
			default: 'image'
		},
		value: {
			type: Object,
			default: () => ({})
		},
		uploadAssignment: String,
		uploadAssignmentId: String | Number,
	},
	provide() {
    return {
      enableTabs: this.enableTabs,
      showTab: this.showTab,
    };
  },
	data() {
		return {
			isUploading: false,
			localCols: { ...this.cols },
			selection: '',
			showUploader: false,
			uploadedImage: null
		}
	},
	mounted() {
		this.showUploader = this.autoShowUploader && !this.value.url;
		window.EventBus.$on('media-manager-dialog', (key) => {
			// if(key === 'cancel') this.showUploader = false;
		});
		this.$nextTick(() => {
			const bwImage = this.$refs.bwImage;
			if(this.isInsideVDialog(bwImage)) {
				this.localCols = {
					lg: 6,
					md: 6,
					sm: 6
				};
			}
		});
	},
	computed: {
		enableTabs() {
			return this.type;
		},
		hasImage() {
			return this.value?.url && !this.value.url.includes('placehold.co');
		},
		isGoogle() {
			if(this.value?.url) {
				try {
					const urlObject = new URL(this.value.url);
					const host = urlObject.hostname;
					return host.includes('google');
				}
				catch (error) {
					// likely just our placeholder '/images/placehold.co.png'
					return false;
				}
			}
			return false;
		},
		showTab() {
			return this.type;
		},
	},
	methods: {
		applyImg() {
			const media = {
				...this.uploadedImage[0], ...this.uploadedImage[0].cropperSettings ?? {}, ...{
					media_id: this.uploadedImage[0].media_id || this.uploadedImage[0].id,
					id: this.value.id,
					location: this.value.location,
					name: this.value.name,
					minDimensions: this.value.minDimensions,
					caption: this.value.caption || ''
				}
			};

			if(this.uploadAssignment)
				media.assignment = this.uploadAssignment;
			if(this.uploadAssignmentId)
				media.assignment_id = this.uploadAssignmentId;

			this.value.url = media.url;
			this.$emit('input', media);
			if (this.showTab !== 'image') this.showUploader = false;
		},
		closeCropper() {
			this.showUploader = false;
			window.EventBus.$emit('media-manager-dialog', 'close');
		},
		changeImg() {
			this.showUploader = true;
			this.uploadedImage = [];
		},
		isInsideVDialog(element) {
			return !!element.closest('.v-dialog');
		},
		recropImg() {
			this.showUploader = true;
			this.uploadedImage = [cloneDeep(this.value)];
			this.$nextTick(() => {
				window.EventBus.$emit('image-recrop');
			});
		},
		removeImage() {
			this.uploadedImage = null;
			this.$emit('delete');
		}
	},
	watch: {
		hasImage: {
			handler(newVal) {
				this.showUplaoder = !newVal;
			}
		},
	}
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

.select-image {
	// border: solid 1px rgba(0, 0, 0, 0.38);
}

.remove-image {
	position: absolute;
	top: 0.5rem;
	right: 0.5rem;
	z-index: 2;
}

::v-deep .cropper-line {
	background-color: var(--v-accent-base) !important;
}

::v-deep .cropper-point.point- {
	&n, &e, &s, &w, &ne, &se, &sw, &nw {
		background-color: var(--v-accent-base) !important;
		height: 12px;
		width: 12px;

		@media #{map-get($display-breakpoints, 'sm-only')} {
			height: 10px;
			width: 10px;
		}

		@media #{map-get($display-breakpoints, 'md-only')} {
			height: 8px;
			width: 8px;
		}

		@media #{map-get($display-breakpoints, 'lg-and-up')} {
			height: 6px;
			width: 6px;
		}
	}

	&n {
		margin-left: -6px;
	}

	&e {
		margin-top: -6px;
	}

	&s {
		margin-left: -6px;
	}

	&w {
		margin-top: -6px;
	}

	@media #{map-get($display-breakpoints, 'sm-only')} {
		&n {
			margin-left: -5px;
		}
		&e {
			margin-top: -5px;
		}
		&s {
			margin-left: -5px;
		}
		&w {
			margin-top: -5px;
		}
	}

	@media #{map-get($display-breakpoints, 'md-only')} {
		&n {
			margin-left: -4px;
		}
		&e {
			margin-top: -4px;
		}
		&s {
			margin-left: -4px;
		}
		&w {
			margin-top: -4px;
		}
	}
}

</style>